import React, { useEffect, useRef } from "react";
import { useAccessContext } from "@/contexts/AccessContext";
import { useMap } from "react-leaflet";
import GeoJsonLayer from "@/components/map/GeoJsonLayer";
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx";
import { Box, Typography } from "@mui/material";
import BlueSwitch from "@/components/ui/BlueSwitch";
import * as turf from "@turf/turf";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { coreGet } from "@/services/coreApi";
import { openMapCard, useCloseMapCardWhenHidden } from "@/features/map/mapCardStore";
import { createGisCluster, gisMarkerIcon } from "@/features/gis/gisCluster";
import { GIS_MARKERS } from "@/features/gis/gisMarkers";

const OPEN_ICON = gisMarkerIcon(GIS_MARKERS.viaOpen.Icon, GIS_MARKERS.viaOpen.color, "open-area-icon");
const CLOSED_ICON = gisMarkerIcon(GIS_MARKERS.viaClosed.Icon, GIS_MARKERS.viaClosed.color, "closed-area-icon");

// Function to filter features based on their visibility within the current map viewport.
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds();
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry;
    // Determine visibility based on geometry type:
    // LineString: check if any segment point is within bounds
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) =>
        bounds.contains([coord[1], coord[0]]),
      );
      // MultiLineString: check each line segment
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) =>
        line.some((coord) => bounds.contains([coord[1], coord[0]])),
      );
      // Point: check if the point is within bounds
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]]);
      // Polygon: check each ring coordinate
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) =>
        ring.some((coord) => bounds.contains([coord[1], coord[0]])),
      );
      // Fallback: handle other geometry types generically
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]]);
      }
      return false;
    }
  });
  return filteredFeatures;
}

function OpenSwitch({ feature, onToggleOpen }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Open for routing</Typography>
      <BlueSwitch
        size="small"
        checked={!!feature.properties.open}
        onChange={(e) => onToggleOpen(e.target.checked)}
        slotProps={{ input: { "aria-label": "Open for routing" } }}
      />
    </Box>
  );
}

export default function ViaAreas({
  cwViaAreasGeojson,
  setCwViaAreasGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  setVoyageActive,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext();
  useCloseMapCardWhenHidden(
    "viaarea-",
    switchGisLayersActive && toggleFlags.includes("ViaAreas"),
  );
  const map = useMap();
  const suppressed = useGisSuppressed();
  const features = cwViaAreasGeojson?.features || [];
  // State for the currently selected feature with its popup position.

  // Cluster + cycle tracking
  const viaClusterRef = useRef(null);
  const clearedThisCycleRef = useRef(false);
  const addedKeysRef = useRef(new Set());

  const accessRef = useRef({ accessContext, setAccessContext });
  accessRef.current = { accessContext, setAccessContext };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/via-areas",
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /geo/via-areas error=",
        );
        setCwViaAreasGeojson(data);
      } catch (error) {
        console.error(error);
      }
    };
    load();
  }, [setCwViaAreasGeojson]);

  // Create/remove marker cluster when ViaAreas or GIS toggle changes; clean up previous cluster
  useEffect(() => {
    // Remove previous cluster if present
    if (viaClusterRef.current) {
      viaClusterRef.current.clearLayers();
      map.removeLayer(viaClusterRef.current);
      viaClusterRef.current = null;
    }
    if (toggleFlags.includes("ViaAreas") && switchGisLayersActive) {
      viaClusterRef.current = createGisCluster(map, {
        rgb: GIS_MARKERS.viaClosed.rgb,
        icon: GIS_MARKERS.viaClosed.Icon,
        // Closed as soon as one member is closed; open only when all are.
        look: (group) => {
          const state = group.getAllChildMarkers().every((m) => m.options.viaOpen)
            ? GIS_MARKERS.viaOpen
            : GIS_MARKERS.viaClosed;
          return { rgb: state.rgb, icon: state.Icon };
        },
      });
      viaClusterRef.current.addTo(map);
    }
    return () => {
      if (viaClusterRef.current) {
        viaClusterRef.current.clearLayers();
        map.removeLayer(viaClusterRef.current);
        viaClusterRef.current = null;
      }
    };
  }, [toggleFlags, map, switchGisLayersActive]);

  // Reset per-cycle flags whenever inputs change (forces fresh rebuild of cluster contents)
  useEffect(() => {
    clearedThisCycleRef.current = false;
    addedKeysRef.current = new Set();
  }, [
    cwViaAreasGeojson,
    zoomLevel,
    dragged,
    resized,
    toggleFlags,
    switchGisLayersActive,
  ]);

  // Populate cluster: clear once per cycle, then add centroid markers for visible via-area polygons
  useEffect(() => {
    if (!toggleFlags.includes("ViaAreas")) return;
    if (!switchGisLayersActive) return;
    if (!viaClusterRef.current) return;

    // Clear cluster once per cycle before adding fresh markers
    if (!clearedThisCycleRef.current) {
      viaClusterRef.current.clearLayers();
      clearedThisCycleRef.current = true;
    }

    features.forEach((feature) => {
      // Only polygon/multipolygon features get a centroid marker
      if (!feature) return;
      const type = feature.geometry?.type;
      if (type !== "Polygon" && type !== "MultiPolygon") return;
      const isOpen = !!feature.properties?.open;

      // Compute centroid (fallback to bounds center)
      let latLng = null;
      try {
        const centroid = turf.centroid(feature);
        if (centroid?.geometry?.coordinates) {
          const [lng, lat] = centroid.geometry.coordinates;
          latLng = L.latLng(lat, lng);
        }
      } catch {}
      if (!latLng) {
        try {
          const b = L.geoJSON(feature).getBounds();
          latLng = b.getCenter();
        } catch {}
      }
      if (!latLng) return;

      // De-duplicate by stable key (include open state so toggles rebuild correctly)
      const fid =
        feature.properties?.id ||
        feature.id ||
        feature.properties?.uuid ||
        feature.properties?.gid;
      const fKeyBase =
        fid != null
          ? String(fid)
          : `${latLng.lat.toFixed(6)},${latLng.lng.toFixed(6)}`;
      const fKey = `${fKeyBase}|open:${isOpen}`;
      if (addedKeysRef.current.has(fKey)) return;
      addedKeysRef.current.add(fKey);

      const icon = isOpen ? OPEN_ICON : CLOSED_ICON;

      const marker = L.marker(latLng, { icon, interactive: !suppressed, viaOpen: isOpen });
      if (!suppressed) {
        marker.on("click", () => {
          openCard(feature, latLng);
        });
      }
      viaClusterRef.current.addLayer(marker);
    });
  }, [
    cwViaAreasGeojson,
    toggleFlags,
    map,
    zoomLevel,
    dragged,
    resized,
    switchGisLayersActive,
    suppressed,
  ]);

  // Ensure cluster is removed when ViaAreas toggle is off or GIS layers are globally disabled
  useEffect(() => {
    if (!toggleFlags.includes("ViaAreas") || !switchGisLayersActive) {
      if (viaClusterRef.current) {
        viaClusterRef.current.clearLayers();
        map.removeLayer(viaClusterRef.current);
        viaClusterRef.current = null;
      }
    }
  }, [toggleFlags, map, switchGisLayersActive]);

  // The previous card's onClose runs after the next one opened: only the current card re-enables the planner.
  const openCardKeyRef = useRef(null);
  const openCard = (feature, position) => {
    const key = `viaarea-${feature.properties.id}`;
    openCardKeyRef.current = key;
    setVoyageActive(false);
    openMapCard({
      key,
      title: "Via area",
      rows: [[null, feature.properties.name || "Unnamed Area"], ["ID", feature.properties.id]],
      footer: (
        <OpenSwitch
          feature={feature}
          onToggleOpen={(open) => updateFeatureOpen(feature, open, position)}
        />
      ),
      anchor: position,
      onClose: () => {
        if (openCardKeyRef.current !== key) return;
        openCardKeyRef.current = null;
        setVoyageActive(true);
      },
    });
  };

  // Update a feature's "open" property and update the global GeoJSON state.
  const updateFeatureOpen = (feature, newOpen, position) => {
    setVoyageActive(false);
    feature.properties.open = newOpen;
    setCwViaAreasGeojson((prevGeojson) => {
      if (!prevGeojson?.features) {
        return prevGeojson;
      }
      const updatedFeatures = prevGeojson.features.map((f) => {
        if (f.properties.id === feature.properties.id) {
          return { ...f, properties: { ...f.properties, open: newOpen } };
        }
        return f;
      });
      return {
        ...prevGeojson,
        features: updatedFeatures,
      };
    });
    openCard({ ...feature }, position);
  };

  // Instead of binding an imperative popup to each feature, listen for click events.
  const onEachFeature = (feature, layer) => {
    layer.on("click", (e) => {
      L.DomEvent.stopPropagation(e);
      let position;
      if (layer.getLatLng) {
        position = layer.getLatLng();
      } else if (layer.getBounds) {
        position = layer.getBounds().getCenter();
      }
      openCard(feature, position);
    });
  };

  return !switchGisLayersActive ||
    !toggleFlags.includes("ViaAreas") ||
    cwViaAreasGeojson === null ? null : (
    <>
      <GeoJsonLayer
        key={[JSON.stringify(cwViaAreasGeojson), zoomLevel, dragged, resized]}
        data={filterViewportBoundingbox(map, features)}
        style={(feature) => ({
          fillColor: feature.properties.open ? "green" : "red",
          color: feature.properties.open ? "green" : "red",
          dashArray: "5,5",
        })}
        onEachFeature={onEachFeature}
        interactive
        transparent
      />
    </>
  );
}
